/**
 * The pre-production board: everything the shoot day needs, on the creative.
 * Unlocked when the doctor approves the script; filled by the art director,
 * read by everyone, ticked by the crew on set. Also the shoot page's view of
 * the boards grouped by location, and the on-set feed with the call button.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, MapPin, Lock, Plus, Trash2, FileText, Upload, X, Phone, Camera, Check, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { ThreadChat } from '@/components/creative/ChatBox';
import {
  qk, fetchBoard, patchBoard, tickShot, fetchShootBoards, fetchClientLocations,
  type Board, type BoardShot, type BoardLocation, type Creative, type ShootSession,
} from '@/lib/queries';
import { uploadChatFile } from '@/lib/upload';
import { cn } from '@/lib/cn';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

function shotsPayload(shots: BoardShot[]) {
  return shots.map((s) => ({ id: s.id ?? undefined, what: s.what, who: s.who ?? undefined, location_id: s.location_id ?? undefined }));
}

/** The board on the creative page. */
export default function BoardCard({ creative }: { creative: Creative }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['board', creative.id], queryFn: () => fetchBoard(creative.id), refetchInterval: 15000 });
  const b = q.data;
  const locs = useQuery({ queryKey: qk.clientLocations(creative.client_id), queryFn: () => fetchClientLocations(creative.client_id), enabled: !!b?.can_edit });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['board', creative.id] }); qc.invalidateQueries({ queryKey: ['shoot', creative.id] }); };
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof patchBoard>[1]) => patchBoard(creative.id, body),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!creative.needs_shoot) return null;
  if (q.isLoading || !b) return <Card className="mb-5"><div className="text-muted text-sm">Loading the board...</div></Card>;

  return (
    <Card className="mb-5" data-testid="board-card" data-unlocked={b.unlocked ? 'true' : 'false'}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><ClipboardList size={14} /> Pre-production board</h3>
        {b.unlocked
          ? <span className="text-[11px] text-muted">{b.shots.length ? `${b.shots_done}/${b.shots.length} shots done` : 'Nothing on the shooting list yet'}{b.updated_by_name ? ` · last by ${b.updated_by_name}` : ''}</span>
          : <span className="text-[11px] text-muted inline-flex items-center gap-1"><Lock size={11} /> locked</span>}
      </div>
      {!b.unlocked ? (
        <div className="rounded-lg bg-bg px-3 py-2 text-xs text-muted" data-testid="board-locked">{b.locked_reason}</div>
      ) : (
        <div className="space-y-4">
          <LocationsSection board={b} all={locs.data ?? []} canEdit={b.can_edit} saving={patch.isPending}
            onChange={(ids) => patch.mutate({ location_ids: ids }, { onSuccess: () => toast.success(ids.length ? 'Locations saved' : 'No location picked') })} />
          <ShotsSection creativeId={creative.id} board={b} canEdit={b.can_edit} saving={patch.isPending}
            onSave={(shots) => patch.mutate({ shots: shotsPayload(shots) }, { onSuccess: () => toast.success('Shooting list saved') })} onChanged={invalidate} />
          <DocSection board={b} canEdit={b.can_edit}
            onSet={(doc) => patch.mutate({ doc }, { onSuccess: () => toast.success(doc ? 'Document attached' : 'Document removed') })} />
          <NotesSection board={b} canEdit={b.can_edit} saving={patch.isPending}
            onSave={(talent, props) => patch.mutate({ talent_notes: talent, props_notes: props }, { onSuccess: () => toast.success('Notes saved') })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-bg px-3 py-2" data-testid="board-shoot">
              <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><Camera size={10} /> Shoot</div>
              {b.shoot ? (
                <div className="text-muted-strong">
                  <Link to={`/shoots/${b.shoot.id}`} className="text-accent-400 hover:underline">{b.shoot.title}</Link>
                  <div>{new Date(b.shoot.shoot_at).toLocaleString('en-AE', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })} · {b.shoot.status === 'planned' ? 'scheduled' : b.shoot.status}</div>
                  <div className="text-muted">{b.shoot.crew_names.length ? `Crew: ${b.shoot.crew_names.join(', ')}` : 'No crew yet'}{b.shoot.location ? ` · ${b.shoot.location}` : ''}</div>
                </div>
              ) : <div className="text-muted">Not on a shoot yet. {b.location_ids.length ? 'It can join one now.' : 'Pick a location first; then it can join a shoot.'}</div>}
            </div>
            <div className="rounded-lg bg-bg px-3 py-2" data-testid="board-references">
              <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><FileText size={10} /> References</div>
              <div className="text-muted-strong">{b.reference_count} file{b.reference_count === 1 ? '' : 's'} in the library{b.reference_urls.length ? ` · ${b.reference_urls.length} link${b.reference_urls.length === 1 ? '' : 's'} in the brief` : ''}</div>
              <div className="text-muted">Upload references on this page under Library as "reference"; links live in the brief.</div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function PhotoRow({ loc }: { loc: BoardLocation }) {
  if (!loc.photos.length) return <div className="text-[11px] text-muted mt-1">No photos of this location yet.</div>;
  return (
    <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
      {loc.photos.map((p) => (
        <a key={p.id} href={p.url ?? undefined} target="_blank" rel="noopener noreferrer" className="shrink-0 w-24 h-18 rounded-lg overflow-hidden border border-slate-800 bg-slate-900" title={p.caption ?? p.filename} data-testid="board-location-photo">
          <img src={p.thumbnail_url ?? p.url ?? undefined} alt={p.caption ?? p.filename} className="w-24 h-[72px] object-cover" loading="lazy" />
        </a>
      ))}
    </div>
  );
}

function LocationsSection({ board, all, canEdit, saving, onChange }: { board: Board; all: BoardLocation[] | { id: string; kind: string; name: string; note?: string | null; active: boolean; photos: BoardLocation['photos'] }[]; canEdit: boolean; saving: boolean; onChange: (ids: string[]) => void }) {
  const picked = new Set(board.location_ids);
  const choices = (all as BoardLocation[]).filter((l) => l.active || picked.has(l.id));
  return (
    <div data-testid="board-locations">
      <div className="text-[10px] uppercase tracking-wide text-muted mb-1.5 inline-flex items-center gap-1"><MapPin size={10} /> Where it is filmed</div>
      {canEdit && (
        <div className="flex flex-wrap gap-2 mb-2">
          {choices.length === 0 && <span className="text-xs text-muted">No locations on this clinic yet. Add them on the client page.</span>}
          {choices.map((l) => (
            <label key={l.id} data-testid="board-location-option" data-picked={picked.has(l.id) ? 'true' : 'false'}
              className={cn('inline-flex items-center gap-2 text-xs px-3 py-1.5 min-h-[36px] rounded-full border cursor-pointer', picked.has(l.id) ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted', saving && 'opacity-60')}>
              <input type="checkbox" className="accent-accent-400" disabled={saving} checked={picked.has(l.id)}
                onChange={() => onChange(picked.has(l.id) ? board.location_ids.filter((x) => x !== l.id) : [...board.location_ids, l.id])} />
              {l.name}{l.kind === 'outside' && <span className="text-[10px] opacity-70">outside</span>}{!l.active && <span className="text-[10px] opacity-70">retired</span>}
            </label>
          ))}
        </div>
      )}
      {board.locations.length === 0
        ? <div className="text-xs text-amber-300/90">No location picked yet. The crew will not know where to go.</div>
        : board.locations.map((l) => (
          <div key={l.id} className="rounded-lg bg-bg px-3 py-2 mb-2" data-testid="board-location">
            <div className="flex items-center gap-2"><span className="text-sm text-muted-strong font-medium">{l.name}</span>{l.kind === 'outside' && <span className="text-[10px] text-muted">outside</span>}{!l.active && <span className="text-[10px] text-muted">retired</span>}</div>
            {l.note && <div className="text-xs text-muted whitespace-pre-wrap mt-0.5">{l.note}</div>}
            <PhotoRow loc={l} />
          </div>
        ))}
    </div>
  );
}

function ShotsSection({ creativeId, board, canEdit, saving, onSave, onChanged }: { creativeId: string; board: Board; canEdit: boolean; saving: boolean; onSave: (shots: BoardShot[]) => void; onChanged: () => void }) {
  const [what, setWhat] = useState('');
  const [who, setWho] = useState('');
  const [where, setWhere] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const tick = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => tickShot(creativeId, id, done),
    onSuccess: () => onChanged(),
    onError: (e) => toast.error(errMsg(e)),
  });
  const locName = (id?: string | null) => board.locations.find((l) => l.id === id)?.name;
  const addOne = () => {
    if (!what.trim()) return;
    onSave([...board.shots, { what: what.trim(), who: who.trim() || null, location_id: where || null }]);
    setWhat(''); setWho('');
  };
  const addMany = () => {
    const lines = pasted.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const extra: BoardShot[] = lines.map((l) => {
      const [w, person] = l.split('|').map((x) => x.trim());
      return { what: w.replace(/^[-*\d.)\s]+/, '') || w, who: person || null, location_id: where || null };
    });
    onSave([...board.shots, ...extra]); setPasted(''); setPasting(false);
  };
  const remove = (idx: number) => onSave(board.shots.filter((_, i) => i !== idx));
  const locOptions = [{ value: '', label: 'Any location' }, ...board.locations.map((l) => ({ value: l.id, label: l.name }))];
  return (
    <div data-testid="board-shots">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><ClipboardList size={10} /> Shooting list</div>
        {canEdit && <button type="button" className="text-[11px] text-muted hover:text-accent-400" onClick={() => setPasting((v) => !v)} data-testid="board-paste-toggle">{pasting ? 'Type one at a time' : 'Paste a list'}</button>}
      </div>
      {board.shots.length === 0 && <div className="text-xs text-muted mb-2">Nothing yet. One line per shot: what, who, where.</div>}
      <ul className="space-y-1 mb-2">
        {board.shots.map((s, idx) => (
          <li key={s.id ?? idx} className="flex items-start gap-2 rounded-lg bg-bg px-3 py-2" data-testid="board-shot" data-done={s.done_at ? 'true' : 'false'}>
            <input type="checkbox" className="accent-accent-400 mt-0.5 w-4 h-4" checked={!!s.done_at} disabled={!board.can_tick || !s.id || tick.isPending}
              onChange={() => s.id && tick.mutate({ id: s.id, done: !s.done_at })} data-testid="board-shot-tick" title={s.done_at ? `Done by ${s.done_by_name ?? 'the crew'}` : 'Mark done'} />
            <div className="flex-1 min-w-0 text-xs">
              <div className={cn('text-muted-strong', s.done_at && 'line-through text-muted')}>{s.what}</div>
              <div className="text-muted">{[s.who, locName(s.location_id)].filter(Boolean).join(' · ')}{s.done_at ? ` · done by ${s.done_by_name ?? 'the crew'}` : ''}</div>
            </div>
            {canEdit && <button type="button" className="p-1 text-muted hover:text-red-400" title="Remove" disabled={saving} onClick={() => remove(idx)} data-testid="board-shot-remove" data-compact><X size={12} /></button>}
          </li>
        ))}
      </ul>
      {canEdit && !pasting && (
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
          <Input label="What" placeholder="Wide of the room, doctor walks in" value={what} onChange={(e) => setWhat(e.target.value)} data-testid="board-shot-what" />
          <Input label="Who" placeholder="Dr Sara" value={who} onChange={(e) => setWho(e.target.value)} data-testid="board-shot-who" />
          <Select label="Where" value={where} onChange={setWhere} options={locOptions} />
          <Button size="sm" icon={<Plus size={12} />} disabled={!what.trim() || saving} onClick={addOne} data-testid="board-shot-add">Add</Button>
        </div>
      )}
      {canEdit && pasting && (
        <div className="space-y-2">
          <textarea className="w-full min-h-[96px] rounded-lg bg-bg border border-slate-800 px-3 py-2 text-xs text-muted-strong focus:outline-none focus:border-accent-400" placeholder={'One shot per line. Add the person after a bar:\nWide of the room | Dr Sara\nClose-up of the laser'} value={pasted} onChange={(e) => setPasted(e.target.value)} data-testid="board-paste" />
          <div className="flex items-center gap-2"><Select value={where} onChange={setWhere} options={locOptions} size="sm" className="w-48" /><Button size="sm" icon={<Plus size={12} />} disabled={!pasted.trim() || saving} onClick={addMany} data-testid="board-paste-add">Add these</Button></div>
        </div>
      )}
    </div>
  );
}

function DocSection({ board, canEdit, onSet }: { board: Board; canEdit: boolean; onSet: (doc: { path: string; name: string; content_type: string; size: number } | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  async function pick(files: File[]) {
    const f = files[0]; if (!f) return;
    try {
      setPct(0);
      const a = await uploadChatFile(f, setPct);
      onSet({ path: a.path, name: a.name, content_type: a.content_type, size: a.size });
    } catch (e) { toast.error(errMsg(e)); } finally { setPct(null); }
  }
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="board-doc">
      <span className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1"><FileText size={10} /> Shooting list document</span>
      {board.doc
        ? <><a href={(board.doc as { url?: string }).url} target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline truncate max-w-[40ch]">{String(board.doc.name)}</a>
            {canEdit && <button type="button" className="p-1 text-muted hover:text-red-400" title="Remove document" onClick={() => onSet(null)} data-compact><Trash2 size={12} /></button>}</>
        : <span className="text-muted">none (a PDF or a sheet can sit beside the list)</span>}
      {canEdit && (
        <>
          <input ref={input} type="file" hidden accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.numbers,.pages,application/pdf" data-testid="board-doc-input" onChange={(e) => { const f = Array.from(e.target.files ?? []); e.currentTarget.value = ''; void pick(f); }} />
          <Button size="sm" variant="secondary" icon={<Upload size={12} />} disabled={pct !== null} onClick={() => input.current?.click()}>{pct === null ? (board.doc ? 'Replace' : 'Attach') : `Uploading ${pct}%`}</Button>
        </>
      )}
    </div>
  );
}

function NotesSection({ board, canEdit, saving, onSave }: { board: Board; canEdit: boolean; saving: boolean; onSave: (talent: string, props: string) => void }) {
  const [talent, setTalent] = useState(board.talent_notes ?? '');
  const [props, setProps] = useState(board.props_notes ?? '');
  useEffect(() => { setTalent(board.talent_notes ?? ''); setProps(board.props_notes ?? ''); }, [board.talent_notes, board.props_notes]);
  const dirty = talent !== (board.talent_notes ?? '') || props !== (board.props_notes ?? '');
  const box = 'w-full min-h-[72px] rounded-lg bg-bg border border-slate-800 px-3 py-2 text-xs text-muted-strong focus:outline-none focus:border-accent-400 disabled:opacity-80';
  return (
    <div data-testid="board-notes">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted mb-1 inline-flex items-center gap-1"><Users size={10} /> {board.talent_name ? `${board.talent_name} and talent notes` : 'Talent notes'}</div>
          <textarea className={box} disabled={!canEdit} placeholder="Standing notes: how the doctor likes to be filmed, what to avoid, consents" value={talent} onChange={(e) => setTalent(e.target.value)} data-testid="board-talent-notes" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Wardrobe and props</div>
          <textarea className={box} disabled={!canEdit} placeholder="White coat, no logos, the new laser handpiece, before-and-after prints" value={props} onChange={(e) => setProps(e.target.value)} data-testid="board-props-notes" />
        </div>
      </div>
      {canEdit && dirty && <div className="mt-2"><Button size="sm" icon={<Check size={12} />} loading={saving} onClick={() => onSave(talent, props)} data-testid="board-notes-save">Save notes</Button></div>}
    </div>
  );
}

/** The shoot page: the boards of its creatives, grouped by location. */
export function ShootBoards({ shootId }: { shootId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['shoot-boards', shootId], queryFn: () => fetchShootBoards(shootId), refetchInterval: 15000 });
  const tick = useMutation({
    mutationFn: ({ creativeId, id, done }: { creativeId: string; id: string; done: boolean }) => tickShot(creativeId, id, done),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shoot-boards', shootId] }),
    onError: (e) => toast.error(errMsg(e)),
  });
  const groups = useMemo(() => {
    const boards = q.data ?? [];
    const order: { key: string; loc: BoardLocation | null; boards: Board[] }[] = [];
    const idx = new Map<string, number>();
    for (const b of boards) {
      const targets = b.locations.length ? b.locations : [null];
      for (const l of targets) {
        const key = l ? l.id : 'none';
        if (!idx.has(key)) { idx.set(key, order.length); order.push({ key, loc: l, boards: [] }); }
        order[idx.get(key)!].boards.push(b);
      }
    }
    return order;
  }, [q.data]);
  if (q.isLoading) return null;
  if (!q.data || q.data.length === 0) return null;
  return (
    <Card className="mb-4" data-testid="shoot-boards">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><MapPin size={14} /> The day by location</h3>
        <span className="text-xs text-muted">{q.data.reduce((n, b) => n + b.shots_done, 0)}/{q.data.reduce((n, b) => n + b.shots.length, 0)} shots done</span>
      </div>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key} className="rounded-lg bg-bg px-3 py-2" data-testid="shoot-board-location">
            <div className="flex items-center gap-2"><span className="text-sm text-muted-strong font-medium">{g.loc ? g.loc.name : 'No location picked'}</span>{g.loc?.kind === 'outside' && <span className="text-[10px] text-muted">outside</span>}</div>
            {g.loc?.note && <div className="text-xs text-muted whitespace-pre-wrap">{g.loc.note}</div>}
            {g.loc && <PhotoRow loc={g.loc} />}
            <div className="mt-2 space-y-2">
              {g.boards.map((b) => {
                const shots = b.shots.filter((s) => !s.location_id || s.location_id === g.key || g.key === 'none');
                return (
                  <div key={b.creative_id} className="border-l-2 border-slate-800 pl-3" data-testid="shoot-board-creative">
                    <div className="text-xs"><Link to={`/creatives/${b.creative_id}`} className="font-mono text-accent-400 hover:underline">{b.code}</Link> <span className="text-muted-strong">{b.title}</span>{b.talent_name && <span className="text-muted"> · {b.talent_name}</span>}{b.filmed_at && <span className="text-emerald-300"> · filmed</span>}</div>
                    {(b.talent_notes || b.props_notes) && <div className="text-[11px] text-muted whitespace-pre-wrap mt-0.5">{[b.talent_notes, b.props_notes].filter(Boolean).join('\n')}</div>}
                    {shots.length === 0 ? <div className="text-[11px] text-muted mt-0.5">No shots listed here.</div> : (
                      <ul className="mt-1 space-y-0.5">
                        {shots.map((s) => (
                          <li key={s.id ?? s.what} className="flex items-start gap-2 text-xs" data-testid="shoot-board-shot" data-done={s.done_at ? 'true' : 'false'}>
                            <input type="checkbox" className="accent-accent-400 mt-0.5 w-4 h-4" checked={!!s.done_at} disabled={!b.can_tick || !s.id || tick.isPending}
                              onChange={() => s.id && tick.mutate({ creativeId: b.creative_id, id: s.id, done: !s.done_at })} data-testid="shoot-board-tick" />
                            <span className={cn('text-muted-strong', s.done_at && 'line-through text-muted')}>{s.what}{s.who ? <span className="text-muted"> · {s.who}</span> : null}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {b.doc && <a href={(b.doc as { url?: string }).url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-accent-400 hover:underline mt-1"><FileText size={10} /> {String(b.doc.name)}</a>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** The on-set feed and the call button, on the shoot page. */
export function OnSetCard({ shoot }: { shoot: ShootSession }) {
  const live = shoot.status === 'scheduled' || shoot.status === 'planned' || shoot.status === 'done';
  return (
    <Card className="mb-4" id="feed" data-testid="on-set-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2"><Camera size={14} /> On set</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {shoot.approver_phones.length === 0
            ? <span className="text-[11px] text-muted">The art director has no phone on their profile yet.</span>
            : shoot.approver_phones.map((p) => (
              <a key={p.phone} href={`https://wa.me/${p.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" data-testid="call-approver"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 min-h-[36px] rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
                <Phone size={12} /> Call {p.name}
              </a>
            ))}
        </div>
      </div>
      <p className="text-[11px] text-muted mb-2">{live ? 'Photos and short clips from the phone land here as they happen; the art director is told at once, in the app and on Telegram, and answers in this thread.' : 'The feed opens once the shoot is scheduled.'}</p>
      {live && <ThreadChat kind="shoot" id={shoot.id} defaultOpen />}
    </Card>
  );
}
